import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Input,
  Label,
  Form,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { registerUser, apiError } from "../../store/actions";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";
import profileImg from "../../assets/images/profile-img.png";
import logoImg from "../../assets/images/logo.svg";
const apiUrl = process.env.REACT_APP_API_URL;

const Register = (props) => {
  document.title = "Register | Sunwave";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [sellers, setSellers] = useState([]);
  const [isSalesManager, setIsSalesManager] = useState(false);

  // Fetch sellers when "Seller Manager" is selected
  const fetchSellers = async () => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    const token = authUser?.token;

    try {
      const response = await fetch(`${apiUrl}/api/sellers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data) {
        setSellers(data);
      }
    } catch (error) {
      console.error("Error fetching sellers:", error);
    }
  };

  // Form validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      username: "",
      password: "",
      role: "",
      assignedSellers: [], // To store selected sellers for "Seller Manager"
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email")
        .required("Please Enter Your Email"),
      username: Yup.string().required("Please Enter Your Username"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please Enter Your Password"),
      role: Yup.string().required("Please select a role"),
    }),
    onSubmit: async (values) => {
      try {
        console.log(values);
        const response = await dispatch(registerUser(values));
        if (response.status === 401) {
          handleTokenExpiration();
        }
      } catch (error) {
        console.error("Error registering user:", error);
      }
    },
  });

  // Watch role changes to handle "Seller Manager" logic
  useEffect(() => {
    const selectedRole = validation.values.role;
    if (selectedRole === "seller_manager") {
      setIsSalesManager(true);
      fetchSellers();
    } else {
      setIsSalesManager(false);
    }
  }, [validation.values.role]);

  const AccountProperties = createSelector(
    (state) => state.Account,
    (account) => ({
      user: account.user,
      registrationError: account.registrationError,
      success: account.success,
    })
  );

  const { user, registrationError, success } = useSelector(AccountProperties);

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setTimeout(() => navigate("/dashboard"), 2000);
    }
  }, [success, navigate]);

  const handleTokenExpiration = () => {
    localStorage.removeItem("authUser");
    navigate("/login");
  };

  return (
    <React.Fragment>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark">
          <i className="bx bx-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col className="col-7">
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Create User</h5>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profileImg} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logoImg}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      {user && (
                        <Alert color="success">
                          User Registered Successfully
                        </Alert>
                      )}

                      {registrationError && (
                        <Alert color="danger">{registrationError}</Alert>
                      )}

                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          className="form-control"
                          placeholder="Enter email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={
                            validation.touched.email && validation.errors.email
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Username</Label>
                        <Input
                          name="username"
                          type="text"
                          id="username"
                          className="form-control"
                          placeholder="Enter username"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.username}
                          invalid={
                            validation.touched.username &&
                            validation.errors.username
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.username}
                        </FormFeedback>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Password</Label>
                        <Input
                          name="password"
                          type="password"
                          id="password"
                          className="form-control"
                          placeholder="Enter Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password}
                          invalid={
                            validation.touched.password &&
                            validation.errors.password
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.password}
                        </FormFeedback>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Role</Label>
                        <Input
                          type="select"
                          name="role"
                          id="role"
                          className="form-control"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.role}
                          invalid={
                            validation.touched.role && validation.errors.role
                          }
                        >
                          <option value="">Select Role</option>
                          <option value="admin">Admin</option>
                          <option value="coordinator">Coordinator</option>
                          <option value="seller">Seller</option>
                          <option value="seller_manager">Seller Manager</option>
                          <option value="financial">Financial</option>
                        </Input>
                        <FormFeedback type="invalid">
                          {validation.errors.role}
                        </FormFeedback>
                      </div>

                      {isSalesManager && (
                        <div className="mb-3">
                          <Label className="form-label">Assign Sellers</Label>
                          <div>
                            {sellers.map((seller) => (
                              <div key={seller.id} className="form-check">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`seller-${seller.id}`}
                                  name="assignedSellers"
                                  value={seller.id}
                                  onChange={(e) => {
                                    const selectedSellers = [...validation.values.assignedSellers];
                                    if (e.target.checked) {
                                      selectedSellers.push(seller.id);
                                    } else {
                                      const index = selectedSellers.indexOf(seller.id);
                                      if (index !== -1) selectedSellers.splice(index, 1);
                                    }
                                    validation.setFieldValue("assignedSellers", selectedSellers);
                                  }}
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor={`seller-${seller.id}`}
                                >
                                  {seller.usenamename} ({seller.email})
                                </Label>
                              </div>
                            ))}
                          </div>
                          <FormFeedback type="invalid">
                            {validation.errors.assignedSellers}
                          </FormFeedback>
                        </div>
                      )}

                      <div className="mt-4">
                        <button className="btn btn-primary btn-block" type="submit">
                          Register
                        </button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  Already have an account ?{" "}
                  <Link to="/login" className="fw-medium text-primary">
                    Login
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
